import {
  ToggleSwitch,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ToggleSwitchOnLabel,
  ToggleSwitchOffLabel,
  TableCaption,
  H3,
  Typography,
} from '@vp/swan';
import { usePageModsContext } from '../../contexts/PageModsProvider';
import {
  ITEM_SCOPE,
  ITEM_GROUP_SCOPE,
  ORDER_SCOPE,
  PAGE_SCOPE,
} from '../../../constants/pageModScopes';
import { PageModEntry } from './PageModEntry';
import { useMemo } from 'react';

interface PageMod {
  modRef: {
    family: string;
    id: string;
  };
  scope: string;
  enabled: boolean;
}

export const PageModsDisplay = () => {
  const {
    pageLevelPageUxMods,
    setPageLevelPageUxMods,
    itemLevelPageUxMods,
    setItemLevelPageUxMods,
    orderLevelPageUxMods,
    setOrderLevelPageUxMods,
    itemGroupLevelPageUxMods,
    setItemGroupLevelPageUxMods,
  } = usePageModsContext();

  const scopeModMap = useMemo(
    () =>
      new Map([
        [
          ITEM_SCOPE,
          { mods: itemLevelPageUxMods, setter: setItemLevelPageUxMods },
        ],
        [
          PAGE_SCOPE,
          { mods: pageLevelPageUxMods, setter: setPageLevelPageUxMods },
        ],
        [
          ORDER_SCOPE,
          { mods: orderLevelPageUxMods, setter: setOrderLevelPageUxMods },
        ],
        [
          ITEM_GROUP_SCOPE,
          {
            mods: itemGroupLevelPageUxMods,
            setter: setItemGroupLevelPageUxMods,
          },
        ],
      ]),
    [
      itemLevelPageUxMods,
      setItemLevelPageUxMods,
      pageLevelPageUxMods,
      setPageLevelPageUxMods,
      orderLevelPageUxMods,
      setOrderLevelPageUxMods,
      itemGroupLevelPageUxMods,
      setItemGroupLevelPageUxMods,
    ]
  );

  const toggleModEnabled = (modToToggle: PageMod) => {
    // get the scope (i.e. ITEM, ORDER, ITEM_GROUP, PAGE) for the pageMod to toggle
    const scopeForModToToggle = scopeModMap.get(modToToggle.scope);
    if (scopeForModToToggle) {
      const pageUxModsClone = [...scopeForModToToggle.mods];
      const indexToToggle = pageUxModsClone.findIndex(
        (mod) => mod.modRef === modToToggle.modRef
      );
      if (indexToToggle !== -1 && pageUxModsClone[indexToToggle]) {
        // update the "enabled" state of the mod, and  list of mods for that scope
        pageUxModsClone[indexToToggle].enabled = !modToToggle.enabled;
        scopeForModToToggle.setter(pageUxModsClone);
      }
    }
  };

  return (
    <Table>
      <TableCaption>
        <H3 visuallyHidden>Page mods</H3>
      </TableCaption>
      <TableHead>
        <TableRow>
          <TableCell id="mod-family-label">Family</TableCell>
          <TableCell id="mod-id-label">ID</TableCell>
          <TableCell id="mod-scope-label">Scope</TableCell>
          <TableCell id="mod-enabled-label">Enabled</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {pageLevelPageUxMods
          .concat(itemLevelPageUxMods)
          .concat(orderLevelPageUxMods)
          .concat(itemGroupLevelPageUxMods)
          .map((pageUxMod: PageMod, index: number) => (
            <TableRow key={`page-ux-mod-${index}`}>
              <TableCell textAlign="left">
                <Typography fontSize="small">
                  {pageUxMod.modRef.family}
                </Typography>
              </TableCell>
              <TableCell textAlign="left">
                <Typography fontSize="small">{pageUxMod.modRef.id}</Typography>
              </TableCell>
              <TableCell textAlign="left">
                <Typography fontSize="small">{pageUxMod.scope}</Typography>
              </TableCell>
              <TableCell textAlign="left">
                <ToggleSwitch
                  activated={pageUxMod.enabled}
                  onRequestActivatedChange={() => toggleModEnabled(pageUxMod)}
                  hideText
                  aria-labelledby="mod-enabled-label"
                >
                  <ToggleSwitchOnLabel>Enabled</ToggleSwitchOnLabel>
                  <ToggleSwitchOffLabel>Disabled</ToggleSwitchOffLabel>
                </ToggleSwitch>
              </TableCell>
            </TableRow>
          ))}
        <PageModEntry />
      </TableBody>
    </Table>
  );
};
